import type { ChartDataset, TooltipModel } from 'chart.js';
import type { UnionToIntersection } from 'chart.js/dist/types/utils';
import commaNumber from 'comma-number';
import { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import IncrementBadge from '@/components/Badge/IncrementBadge';
import { Flex, Space, VFlex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import {
  CHART_GRAPH,
  CHART_METRIC_VIEW_TYPE,
  CHART_TERM_UNIT,
  CHART_TOOLTIP_TARGET_TYPE,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import {
  buildTimeSeriesChartFeedbackListModalOptions,
  type TimeSeriesChartDataset,
} from '@/features/(charts)/chart-renderer/lib/build-time-series-chart-feedback-list-modal-options';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import {
  isSameTimeSeriesPeakPoint,
  type TimeSeriesPeakPoint,
} from '@/features/(charts)/chart-renderer/lib/time-series-peak-point';
import { ChartFieldMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';
import { cn } from '@/lib/cn';
import { ChartTooltipShell } from '@/shared/ui/charts';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { getAlphaColor, getRgbaColor } from '@/styles/colors/color.utils';
import {
  DATE_FORMAT,
  getRangeUnitByTermUnit,
  getTimezoneDayjs,
  parseDateFormatByRangeUnit,
} from '@/utils/time';
import { calIncrementAmount } from '@/utils/util';

import { useOpenChartClickDialog } from '../../hooks/use-open-chart-click-dialog';

type TooltipProps = {
  tooltip: TooltipModel<'line'>;
  meta: ChartBase & { name: string };
  tableData: ChartTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
  peakPoints?: TimeSeriesPeakPoint[];
};

const isTimeSeriesChart = (graph: CHART_GRAPH) => {
  const list = [CHART_GRAPH.LINE, CHART_GRAPH.AREA_STACKED, CHART_GRAPH.AREA];
  return list.includes(graph);
};

const isAllowCustomTooltipClick = (graph: CHART_GRAPH) => {
  const list = [CHART_GRAPH.LINE, CHART_GRAPH.AREA_STACKED, CHART_GRAPH.AREA];
  return list.includes(graph);
};

const ChartCustomTooltip = (props: TooltipProps) => {
  const { graph, target, field } = props.meta;
  const tooltipRef = useRef<HTMLDivElement>(null);
  const color = useColor();
  const openChartClickDialog = useOpenChartClickDialog();

  const label = useMemo(() => {
    const data = props.tooltip.dataPoints[0];
    if (isTimeSeriesChart(graph)) {
      if (target.term.unit === CHART_TERM_UNIT.WEEK) {
        const { start, end } = parseDateFormatByRangeUnit(
          data.label,
          getRangeUnitByTermUnit(target.term.unit),
        );
        return `${getTimezoneDayjs(start).format(
          DATE_FORMAT.readable,
        )} - ${getTimezoneDayjs(end).format(DATE_FORMAT.readable)}`;
      }
      return data.label;
    }

    const dataset = data.dataset as ChartCustomAccDataset;

    const isComopareData =
      dataset.date_type === CHART_TOOLTIP_TARGET_TYPE.COMPARE;

    if (isComopareData) {
      return chartUtils.getReadableDateRangeByCompareTerm(
        target.term,
        target.compare_term,
      );
    } else {
      return chartUtils.getReadableDateRangeByTerm(target.term);
    }
  }, [props.tooltip.dataPoints, graph, target.term, target.compare_term]);

  const onMouseOver = useDebouncedCallback((targetIndex: number) => {
    if (target.is_compare) return;
    const chart = props.tooltip.chart;
    const indexArr = props.tooltip.dataPoints.map((p) => {
      return p.datasetIndex;
    });

    chart.data.datasets.forEach((dataset, index) => {
      const isHover = indexArr[targetIndex] === index;
      if (isHover) {
        dataset.borderColor = dataset.backgroundColor;
      } else {
        dataset.borderColor = getRgbaColor(
          dataset.backgroundColor as string,
          0.2,
        );
        dataset.backgroundColor = getRgbaColor(
          dataset.backgroundColor as string,
          0.2,
        );
      }
    });
    chart.update();
  }, 100);

  const openChartRawDetailModal = useCallback(
    (dataPointIndex: number) => {
      const dataPoint = props.tooltip.dataPoints[dataPointIndex];
      const dataIndex = dataPoint.dataIndex;
      const modalOptions: ChartFeedbackListModalOptions<CellFeedbackListModalOptions> = {
        chart: props.meta,
        dashboardCardInfo: props.dashboardCardInfo,
        isAll: false,
        cell: {
          compared_target: false,
          series_index: 0,
          item_x: '',
          item_x_name: '',
        },
      };

      if (isTimeSeriesChart(graph)) {
        const dataset = dataPoint.dataset as TimeSeriesChartDataset;
        const timeSeriesModalOptions =
          buildTimeSeriesChartFeedbackListModalOptions({
            chart: props.meta,
            dashboardCardInfo: props.dashboardCardInfo,
            dataset,
            dataIndex,
            dataLabel: dataPoint.label,
            startPeakAnalysisOnFeedbackOpen: props.peakPoints?.some(
              (peakPoint) =>
                isSameTimeSeriesPeakPoint(
                  dataPoint.raw,
                  peakPoint,
                  dataPoint.label,
                  dataPoint.datasetIndex,
                ),
            ),
          });

        openChartClickDialog({
          alias: JSON.stringify(timeSeriesModalOptions),
          type: DETAIL_MODAL_TYPE.CHART,
          idx: 0,
          options: timeSeriesModalOptions,
        });
        return;
      }
      if (graph === CHART_GRAPH.COMBO_CHART) {
        const dataset = dataPoint.dataset as UnionToIntersection<
          ChartDataset<'line'>
        > & {
          date_type: CHART_TOOLTIP_TARGET_TYPE;
          meta: {
            x: StringNumberValue[];
            y: StringNumberValue[];
          };
        };

        const cellOptions: CellOptionsWithName = {
          compared_target: false,
          series_index: 1,
          item_x: dataset.meta.x[dataIndex],
          item_x_name: dataPoint.label,
        };

        modalOptions.cell = cellOptions;

        openChartClickDialog({
          alias: JSON.stringify(modalOptions),
          type: DETAIL_MODAL_TYPE.CHART,
          idx: 0,
          options: modalOptions,
        });
        return;
      }
    },
    [
      graph,
      props.dashboardCardInfo,
      props.meta,
      props.peakPoints,
      props.tooltip.dataPoints,
      openChartClickDialog,
    ],
  );

  const body = useMemo(() => {
    if (props.meta.type === CHART_TYPE.CROSS_AXIS) {
      return (
        <CrossAxisTooltipBody
          tooltip={props.tooltip}
          meta={props.meta}
          openChartRawDetailModal={openChartRawDetailModal}
          onMouseOver={onMouseOver}
        />
      );
    }
    if (graph === CHART_GRAPH.COMBO_CHART) {
      return (
        <ComboChartTooltipBody
          tooltip={props.tooltip}
          meta={props.meta}
          openChartRawDetailModal={openChartRawDetailModal}
        />
      );
    }
    if (graph === CHART_GRAPH.BAR_STACKED) {
      return (
        <StackedTooltipBody
          tooltip={props.tooltip}
          meta={props.meta}
          openChartRawDetailModal={openChartRawDetailModal}
        />
      );
    }
    if (isAllowCustomTooltipClick(graph)) {
      return (
        <LineChartTooltipBody
          tooltip={props.tooltip}
          meta={props.meta}
          openChartRawDetailModal={openChartRawDetailModal}
          onMouseOver={onMouseOver}
        />
      );
    }

    return (
      <BarchartTooltipBody
        tooltip={props.tooltip}
        meta={props.meta}
        openChartRawDetailModal={openChartRawDetailModal}
      />
    );
  }, [props.meta, props.tooltip, graph, openChartRawDetailModal, onMouseOver]);

  const crossFieldLabel = useMemo(() => {
    return props.tooltip.dataPoints[0]?.label || '';
  }, [props.tooltip.dataPoints]);

  return (
    <TooltipStyle
      ref={tooltipRef}
      interactive={isAllowCustomTooltipClick(graph)}
      tooltip={props.tooltip}
    >
      <Typo type="label3" color={color.gray700}>
        {label}
      </Typo>
      <Space h={18} />
      {props.meta.series[0]?.cross_field ? (
        <VFlex>
          <Typo type="label3" color={color.gray600}>
            {ChartFieldMap.get(field.type)}
          </Typo>
          <Space h={8} />
          <Typo type="label3" weight="sb" color={color.gray800}>
            {crossFieldLabel}
          </Typo>
          <Space h={12} />
          <Typo type="label3" color={color.gray600}>
            {ChartFieldMap.get(props.meta.series[0]?.cross_field?.type)}
          </Typo>
        </VFlex>
      ) : (
        <Typo type="label3" color={color.gray600}>
          {ChartFieldMap.get(field.type)}
        </Typo>
      )}

      <Space h={8} />
      <VFlex g={8}>{body}</VFlex>
    </TooltipStyle>
  );
};

const TooltipStyle = styled(ChartTooltipShell)`
  .rect {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background-color: ${(props) => props.theme.colors.gray300};
  }
`;

const LegendStyle = styled(Flex)`
  flex: 1;
  cursor: pointer;
  padding: 4px 8px;
  white-space: nowrap;
  background-color: ${(props) => props.theme.colors.ND04};
  border-radius: 4px;
  transition: all 0.2s;
  &:hover {
    background-color: ${(props) => props.theme.colors.ND08};
  }
`;

const CrossAxisTooltipBody = (props: {
  tooltip: TooltipModel<'line'>;
  meta: ChartBase;
  openChartRawDetailModal: (dataPointIndex: number) => void;
  onMouseOver: (targetIndex: number) => void;
}) => {
  const color = useColor();
  const { tooltip, meta, openChartRawDetailModal, onMouseOver } = props;
  const { target } = meta;
  return tooltip.body.map((item, i) => {
    const nodeData = tooltip.dataPoints[i];
    const colorString = nodeData.dataset.backgroundColor as string;
    const bgColor =
      typeof colorString === 'string' ? colorString : color.primary100;

    let compareDataset: ChartCustomAccDataset | null = null;
    if (target.is_compare) {
      const currDataset = nodeData.chart.data.datasets[
        nodeData.datasetIndex
      ] as ChartCustomAccDataset;

      const date_type = currDataset.date_type;

      const datasets = props.tooltip.chart.data
        .datasets as ChartCustomAccDataset[];
      compareDataset = datasets
        .filter((dataset) => dataset.meta.y === currDataset.meta.y)
        .find(
          (dataset) =>
            dataset.date_type ===
            (date_type === CHART_TOOLTIP_TARGET_TYPE.COMPARE
              ? CHART_TOOLTIP_TARGET_TYPE.CURRENT
              : CHART_TOOLTIP_TARGET_TYPE.COMPARE),
        );
    }

    let compareValue: number | null = null;
    if (compareDataset) {
      compareValue = compareDataset.data[nodeData.dataIndex] || null;
    }

    const currentValue = Number(
      ((nodeData.dataset?.data?.[nodeData.dataIndex] as number) || 0).toFixed(
        1,
      ),
    );
    const currentLabel = nodeData.dataset.label;

    return (
      <LegendStyle
        g={8}
        a="center"
        key={i}
        onClick={() => openChartRawDetailModal(i)}
        onMouseEnter={() => onMouseOver(i)}
      >
        <div
          className="rect"
          style={{
            backgroundColor: bgColor,
          }}
        ></div>
        <Typo type="label3" color={color.gray800} weight="sb">
          {currentLabel} : {commaNumber(currentValue)}
          {props.meta.series[0]?.options?.view_type ===
          CHART_METRIC_VIEW_TYPE.PERCENTAGE
            ? '%'
            : ''}
        </Typo>
        {compareValue !== null && (
          <IncrementBadge
            amount={calIncrementAmount(
              Number(item.lines[0].split(':')[1].replaceAll(',', '')) || 0,
              compareValue,
            )}
          />
        )}
      </LegendStyle>
    );
  });
};

const LineChartTooltipBody = (props: {
  tooltip: TooltipModel<'line'>;
  meta: ChartBase;
  openChartRawDetailModal: (dataPointIndex: number) => void;
  onMouseOver: (targetIndex: number) => void;
}) => {
  const color = useColor();
  const { tooltip, meta, openChartRawDetailModal, onMouseOver } = props;
  const { target } = meta;
  return tooltip.body.map((item, i) => {
    const nodeData = tooltip.dataPoints[i];
    const colorString = nodeData.dataset.backgroundColor as string;
    const bgColor =
      typeof colorString === 'string' ? colorString : color.primary100;

    let compareDataset: ChartCustomAccDataset | null = null;
    if (target.is_compare) {
      const currDataset = nodeData.chart.data.datasets[
        nodeData.datasetIndex
      ] as ChartCustomAccDataset;

      const date_type = currDataset.date_type;
      const compareDatasetIndexOffset =
        date_type === CHART_TOOLTIP_TARGET_TYPE.CURRENT ? 1 : -1;
      const datasets = tooltip.chart.data.datasets as ChartCustomAccDataset[];
      compareDataset =
        datasets[nodeData.datasetIndex + compareDatasetIndexOffset] || null;
    }

    let compareValue: number | null = null;
    if (compareDataset) {
      compareValue = compareDataset.data[nodeData.dataIndex].y || null;
    }

    const currentValue = nodeData.parsed.y;

    return (
      <LegendStyle
        g={8}
        a="center"
        key={i}
        onClick={() => openChartRawDetailModal(i)}
        onMouseEnter={() => onMouseOver(i)}
      >
        <div
          className="rect"
          style={{
            backgroundColor: bgColor,
          }}
        ></div>
        <Typo type="label3" color={color.gray600} weight="sb">
          {item.lines.map((node) => node)}
          {props.meta.series[0]?.options?.view_type ===
          CHART_METRIC_VIEW_TYPE.PERCENTAGE
            ? '%'
            : ''}
        </Typo>
        {compareValue !== null && (
          <IncrementBadge
            amount={calIncrementAmount(currentValue || 0, compareValue)}
          />
        )}
      </LegendStyle>
    );
  });
};

const BarchartTooltipBody = (props: {
  tooltip: TooltipModel<'line'>;
  meta: ChartBase;
  openChartRawDetailModal: (dataPointIndex: number) => void;
}) => {
  const color = useColor();
  const { tooltip, meta, openChartRawDetailModal } = props;
  const { target } = meta;
  return tooltip.dataPoints.map((item, i) => {
    let compareDataset: ChartCustomAccDataset | null = null;
    const currDataset = item.dataset as ChartCustomAccDataset;
    const date_type = currDataset.date_type;
    const datasets = tooltip.chart.data.datasets as ChartCustomAccDataset[];
    if (target.is_compare) {
      compareDataset =
        datasets.find(
          (dataset) =>
            dataset.date_type ===
            (date_type === CHART_TOOLTIP_TARGET_TYPE.COMPARE
              ? CHART_TOOLTIP_TARGET_TYPE.CURRENT
              : CHART_TOOLTIP_TARGET_TYPE.COMPARE),
        ) || null;
    }

    let compareValue: number | null = null;
    if (compareDataset) {
      compareValue = compareDataset.data[item.dataIndex] || null;
    }

    const bgColorArr = item.dataset.backgroundColor as string[];
    const bgColor = bgColorArr[item.dataIndex];

    const isPercentView =
      props.meta.series[0]?.options?.view_type ===
      CHART_METRIC_VIEW_TYPE.PERCENTAGE;

    const total = item.dataset.data.reduce(
      (acc: number, curr) => acc + (isNaN(Number(curr)) ? 0 : Number(curr)),
      0,
    );

    const value = Number(Number(item.raw).toFixed(1)) || 0;

    const currentValue = isPercentView
      ? Number(((value / total) * 100).toFixed(1))
      : value;

    return (
      <LegendStyle
        g={8}
        a="center"
        key={i}
        onClick={() => openChartRawDetailModal(0)}
      >
        <div
          className="rect"
          style={{
            backgroundColor: bgColor,
          }}
        ></div>
        <Typo type="label3" color={color.gray600} weight="sb">
          {item.label} : {commaNumber(currentValue)}
          {isPercentView ? '%' : ''}
        </Typo>
        {compareValue !== null && (
          <IncrementBadge amount={calIncrementAmount(value, compareValue)} />
        )}
      </LegendStyle>
    );
  });
};

const StackedTooltipBody = (props: {
  tooltip: TooltipModel<'line'>;
  meta: ChartBase;
  openChartRawDetailModal: (dataPointIndex: number) => void;
}) => {
  const color = useColor();
  const { tooltip, meta, openChartRawDetailModal } = props;
  const { target } = meta;
  return tooltip.dataPoints.map((item, i) => {
    let compareDataset: ChartCustomAccDataset | null = null;
    const currDataset = item.dataset as ChartCustomAccDataset;
    const date_type = currDataset.date_type;
    const datasets = tooltip.chart.data.datasets as ChartCustomAccDataset[];
    if (target.is_compare) {
      compareDataset =
        datasets.find(
          (dataset) =>
            dataset.date_type ===
            (date_type === CHART_TOOLTIP_TARGET_TYPE.COMPARE
              ? CHART_TOOLTIP_TARGET_TYPE.CURRENT
              : CHART_TOOLTIP_TARGET_TYPE.COMPARE),
        ) || null;
    }

    let compareValue: number | null = null;
    if (compareDataset) {
      compareValue = compareDataset.data[item.dataIndex] || null;
    }

    const bgColor = item.dataset.backgroundColor as string;

    const currentValue = Number(Number(item.raw).toFixed(1)) || 0;

    return (
      <LegendStyle
        g={8}
        a="center"
        key={i}
        onClick={() => openChartRawDetailModal(0)}
      >
        <div
          className="rect"
          style={{
            backgroundColor: bgColor,
          }}
        ></div>
        <Typo type="label3" color={color.gray600} weight="sb">
          {item.dataset.label} : {commaNumber(currentValue)}
          {props.meta.series[0]?.options?.view_type ===
          CHART_METRIC_VIEW_TYPE.PERCENTAGE
            ? '%'
            : ''}
        </Typo>
        {compareValue !== null && (
          <IncrementBadge
            amount={calIncrementAmount(currentValue, compareValue)}
          />
        )}
      </LegendStyle>
    );
  });
};

const ComboChartTooltipBody = (props: {
  tooltip: TooltipModel<'line'>;
  meta: ChartBase;
  openChartRawDetailModal: (dataPointIndex: number) => void;
}) => {
  const color = useColor();
  const { tooltip } = props;

  // 바 차트와 라인 차트 데이터를 분리하여 정렬된 순서로 표시
  const sortedDataPoints = tooltip.dataPoints.sort((a, b) => {
    const aDataset = a.dataset as any;
    // 바 차트(Primary)를 먼저, 라인 차트(Secondary)를 나중에
    return aDataset.yAxisID === 'y' ? -1 : 1;
  });

  return (
    <div className="flex flex-col gap-1">
      <Typo type="label3" color={color.gray600} weight="sb" className="mb-2">
        {tooltip.title[0]}
      </Typo>
      {sortedDataPoints.map((dataPoint, index) => {
        const dataset = dataPoint.dataset as any;
        const isLineChart = dataset.type === 'line';

        const bgColor = isLineChart
          ? getAlphaColor(color.gray700, 0.2)
          : dataset.backgroundColor[dataPoint.dataIndex];

        const currentValue = isLineChart
          ? Number((Number(dataPoint.parsed?.y) || 0).toFixed(1))
          : Number((Number(dataPoint.raw) || 0).toFixed(1));

        return (
          <LegendStyle key={index} g={8} a="center">
            <div
              className={cn('rect', isLineChart && 'border-2 border-gray-700')}
              style={{
                backgroundColor: bgColor,
              }}
            />
            <VFlex g={2}>
              <Typo type="label3" color={color.gray800} weight="sb">
                {dataPoint.dataset.label} : {commaNumber(currentValue)}
                {props.meta.series[0]?.options?.view_type ===
                CHART_METRIC_VIEW_TYPE.PERCENTAGE
                  ? '%'
                  : ''}
              </Typo>
            </VFlex>
          </LegendStyle>
        );
      })}
    </div>
  );
};

export default ChartCustomTooltip;
